import { defineComponent, computed, toRef, Ref } from 'vue';
import {
    EntryModel, HoursEntryModel, HoursOfOperationDisplayType,
    HoursOfOperationHolidaysCondition, HoursOfOperationType, Listing,
    ListingUtilities, ListingAfterHoursMessageType
} from '@truckdown/locations';
import { mergeMidnightEntriesFromApi } from '../../utilities';

interface IDayInfo {
    entries: string[],
    message: string
}

const getDisplay = function (listing: Listing, day?: HoursEntryModel): IDayInfo {
    var type = ListingUtilities.getAfterHoursStatusType(listing);
    return {
        entries: getTimeEntry(listing, type, day),
        message: getMessage(listing, type, day)
    };
}

const getTimeEntry = function (listing: Listing, type: ListingAfterHoursMessageType, day?: HoursEntryModel): string[] {
    if (listing.hoursData.type == HoursOfOperationType.Closed)
        return [ 'Closed' ];

    if (listing.hoursData.type == HoursOfOperationType.AlwaysOpen)
        return ['Open 24hrs'];

    if (listing.hoursData.type == HoursOfOperationType.SelectHours) {
        if (day && day.enabled) {
            var entries = day.entries
                .filter((e) => {
                    return e.type == HoursOfOperationDisplayType.Open
                        || !(e.type == HoursOfOperationDisplayType.AfterHours && day.afterHours24);
                })
                .map((e) => {
                    return getTimeEntryValue(e, type);
                });

            if (entries.length > 0) {
                return entries;
            }

            return ['Closed'];
        }

        return ['Closed'];
    }

    return ['Unknown'];
}

const getTimeEntryValue = function (entry: EntryModel, type: ListingAfterHoursMessageType): string {
    let suffix = entry.type == HoursOfOperationDisplayType.AfterHours
        ? ListingUtilities.getAfterHoursListingHoursEntrySuffix(type)
        : '';
    return getTime(entry.startTime) + ' to ' + getTime(entry.endTime) + suffix;
}

const getTime = function (time: string): string {
    if (time == null) return "";

    const components = time.split(':');
    if (components.length === 2) {
        let hour = Number(components[0]);
        let minutes = components[1];

        if (isNaN(hour)) {
            return time;
        }

        const format = (n: number) => (n < 10 ? '0' + n : '' + n);

        if (hour === 0) {
            return `12:${minutes} AM`;
        } else if (hour === 12) {
            return `12:${minutes} PM`;
        } else if (hour > 12) {
            return `${format(hour - 12)}:${minutes} PM`;
        } else {
            return `${format(hour)}:${minutes} AM`;
        }
    }

    return time;
};

const getMessage = function (listing: Listing, type: ListingAfterHoursMessageType, day?: HoursEntryModel) {
    if (day) {
        if (listing.hoursData.type == HoursOfOperationType.SelectHours
            && day.enabled && day.afterHours24) {
            return ListingUtilities.getAfterHoursListing24HourEntryMessage(type);
        }
    }
    return '';
}

const getComputed = function (listingRef: Ref<Listing>) {
    const allEntries = computed(() => {
        const entries = listingRef.value.hoursData?.entries || {};
        return mergeMidnightEntriesFromApi(entries) as { [key: string]: HoursEntryModel };
    });

    return {
        showClosedHolidayMessage: computed(() => {
            return listingRef.value.hoursData.majorHolidayCondition == HoursOfOperationHolidaysCondition.Closed;
        }),
        showDifferentHolidayMessage: computed(() => {
            return listingRef.value.hoursData.majorHolidayCondition == HoursOfOperationHolidaysCondition.LimitedServices;
        }),
        monday: computed(() => {
            return getDisplay(listingRef.value, allEntries.value["Monday"]);
        }),
        tuesday: computed(() => {
            return getDisplay(listingRef.value, allEntries.value["Tuesday"]);
        }),
        wednesday: computed(() => {
            return getDisplay(listingRef.value, allEntries.value["Wednesday"]);
        }),
        thursday: computed(() => {
            return getDisplay(listingRef.value, allEntries.value["Thursday"]);
        }),
        friday: computed(() => {
            return getDisplay(listingRef.value, allEntries.value["Friday"]);
        }),
        saturday: computed(() => {
            return getDisplay(listingRef.value, allEntries.value["Saturday"]);
        }),
        sunday: computed(() => {
            return getDisplay(listingRef.value, allEntries.value["Sunday"]);
        })
    };
}

export default defineComponent({
    props: {
        listing: {
            type: Object as () => Listing,
            required: true
        },
        editable: {
            type: Boolean,
            default: false,
        }
    },
    emits: [],
    setup: function (props, ctx) {
        const listingRef = toRef(props, 'listing');

        // Now pass listingRef to getComputed to keep computed reactive
        const computedData = getComputed(listingRef);
        return {
            ...computedData
        };
    }
});

